import type { BaseQueryApi } from "@reduxjs/toolkit/query";
import type { AppThunkDispatch, CommonRootState } from "@common/types/common-root-state-type";
// Type-only import: erased at runtime, so this module stays free of the heavy
// backend-api graph and is unit-testable in isolation.
import type { ApiBaseQuery } from "./backend-api";

export type ReauthAction = (dispatch: AppThunkDispatch) => Promise<void>;

/**
 * A 401 is re-auth-recoverable only in the boot/hydration race: redux holds NO
 * token, so prepareHeaders had nothing to attach (its hub fallback also came up
 * empty). If redux DOES hold a token the backend rejected a *present* token —
 * re-authenticating with the same token won't help, and running the full login
 * bootstrap (clearState + data reload + socket reconnect) on a live engine for
 * a stray 401 would be harmful. So we only re-auth when the token is absent.
 * The `authenticate` endpoint is excluded so re-auth never recurses on itself.
 */
const isRecoverable401 = (api: BaseQueryApi): boolean => {
  if (api.endpoint === "authenticate") return false;
  return !(api.getState() as CommonRootState)?.UserReducer?.accessToken;
};

/**
 * wa-scheduler#129 (fix #2). The access token is a permanent JWT (backend
 * jwtOptions.expiresIn = "100y") persisted in the authoritative chrome.storage
 * hub. At boot a request can still fire BEFORE redux hydrates AND before the
 * MV3 service worker answers `get-token`, so prepareHeaders attaches no token
 * and the request 401s. The fire-and-forget wa-message-listener-event POST has
 * no retry, so that inbound event is dropped forever and only self-heals on a
 * lucky reboot — the ~2% residual left after the prepareHeaders hub-fallback.
 *
 * This wrapper narrows the residual: on a recoverable 401 (see `isRecoverable401`)
 * it runs `reauth` once — concurrent 401s share the one in-flight promise — then
 * retries the original request a single time. It recovers events whose token
 * seeds into the hub during the reauth window; a request that stays tokenless
 * (hub never seeds in time) still settles to 401, same as before — but is no
 * longer silently dropped in the common recoverable case. Retry is safe: a 401
 * means the backend rejected the request before processing it (auth is gated in
 * a before-hook), so re-delivery is not a duplicate.
 */
export function withReauth(baseQuery: ApiBaseQuery, reauth: ReauthAction): ApiBaseQuery {
  let inFlight: Promise<void> | null = null;
  return async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error?.status === 401 && isRecoverable401(api)) {
      if (!inFlight) {
        inFlight = reauth(api.dispatch).finally(() => {
          inFlight = null;
        });
      }
      try {
        await inFlight;
      } catch {
        // reauth failed — retry below re-401s and settles.
      }
      result = await baseQuery(args, api, extraOptions);
    }
    return result;
  };
}
